import { useStripe } from "@stripe/react-stripe-js";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import CREATE_SUBSCRIPTION from "graphql/mutations/CreateSubscription";
import GET_ACTIVE_SUBSCRIPTION from "graphql/queries/GetActiveSubscription";
import GET_MY_CREDITS from "graphql/queries/GetMyCredits";

import { trackTapfiliateConversion } from "utils/tapfiliate";

const trackPaymentSuccess = (metaData: any) =>
  trackTapfiliateConversion(
    metaData.id,
    metaData.latest_invoice.total * 0.01,
    metaData.customer,
    metaData.latest_invoice.currency
  );
interface StripeSubscriptionCreationParams {
  priceId: string;
  adInfo?: string | null;
  paymentMethodId: string;
  promotionCode?: string;
}

export default () => {
  const { t } = useTranslation("error");
  const stripe = useStripe();
  const [createStripeSubscription, { loading, data: subscriptionData }] =
    useMutation(CREATE_SUBSCRIPTION, {
      // refetch data in My Credit pages
      refetchQueries: [
        {
          query: GET_ACTIVE_SUBSCRIPTION,
        },
        {
          query: GET_MY_CREDITS,
        },
      ],
    });

  const createSubscription = async ({
    priceId,
    adInfo,
    paymentMethodId,
    promotionCode,
  }: StripeSubscriptionCreationParams): Promise<any> => {
    const { data } = await createStripeSubscription({
      variables: {
        input: {
          attributes: { priceId, paymentMethodId, adInfo, promotionCode },
        },
      },
    });

    const { subscription } = data.createSubscription;
    const { metaData, status } = subscription;
    const paymentIntent = metaData.latest_invoice.payment_intent;

    if (!stripe) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // below is the logic to handle the result of subscribing to a new program
    // read more in Stripe's doc https://stripe.com/docs/billing/subscriptions/overview

    if (status === "active") {
      trackPaymentSuccess(metaData);
    }

    if (status === "incomplete") {
      if (paymentIntent.status === "requires_action") {
        // in this case, Stripe requires user to do 3D Secure confirmation
        // https://stripe.com/docs/billing/subscriptions/overview#requires-action
        const result = await stripe.confirmCardPayment(
          paymentIntent.client_secret,
          {
            payment_method: paymentMethodId,
          }
        );

        if (result.error) {
          throw result.error;
        } else if (result?.paymentIntent?.status === "succeeded") {
          trackPaymentSuccess(metaData);
        } else {
          throw new Error(
            "Something wrong happened. Your payment cannot be processed."
          );
        }
      }

      if (paymentIntent.status === "requires_payment_method") {
        // This happends when the bank rejected the card. User need to add a new one in
        // Fancier thing would be the user add a new card and we proceed them with the pending payment
        // Read the fancier thing here https://stripe.com/docs/billing/subscriptions/overview#requires-payment-method
        // Here I just let the payment failed
        // TODO: Do the fancier thing, which requires to save the pending payment in localStorage
        throw new Error(
          t(
            `Your credit card was declined by the bank. Try again. If it still does not work, add another credit card in "MY BILLING", make it your default payment method and try again.`
          )
        );
      }
    }
  };

  return { createSubscription, loading, data: subscriptionData };
};
